import { Steps } from 'antd';

import InvestorData from '../InvestorData/InvestorData';
import Fatca from '../Fatca/Fatca';
import CRS from '../CRS/CRS';
import Beneficiaries from '../Beneficiaries/Beneficiaries';
import TaxHolder from '../TaxHolder/TaxHolder';
import LegalInvestorStatus from '../LegalInvestorStatus/LegalInvestorStatus';
import PhysicalInvestorStatus from '../PhysicalInvestorStatus/PhysicalInvestorStatus';
import LegalInvestorForm from '../LegalInvestorForm/LegalInvestorForm';
import PhysicalInvestorForm from '../PhysicalInvestorForm/PhysicalInvestorForm';
import SuitabilityTest from '../SuitabilityTest/SuitabilityTest';
import CreateSignature from '../../CreateSignature/CreateSignature';
import DistributorData from '../DistributorData/DistributorData';
import Contacts from '../Contacts/Contacts';
import RepresentativesForm from '../RepresentativesForm/RepresentativesForm';
import PhysicalInvestorDocuments from '../PhysicalInvestorDocuments/PhysicalInvestorDocuments';
import LegalInvestorDocuments from '../LegalInvestorDocuments/LegalInvestorDocuments';

import useStepper from '../../../Hooks/useStepper/useStepper';

import { InvestorAPI } from '../../../Api/InvestorAPI';
import { InstitutionsAPI } from '../../../Api/InstitutionsAPI';
import { ContractAPI } from '../../../Api/ContractAPI';

import { IStepper } from './IStepper';

import './Stepper.scss';

const { Step } = Steps;

const Stepper = ({
	baseURL,
	baseURLExternal,
	translations,
	userIdInstitution,
	userEmail,
	language,
	clientId,
	isInvestor,
	moduleRole,
}: IStepper) => {
	const institutionsAPI = InstitutionsAPI(baseURL, baseURLExternal, clientId);
	const investorsAPI = InvestorAPI(baseURL, baseURLExternal, clientId);
	const contractAPI = ContractAPI(baseURL, baseURLExternal, clientId);
	const isReadOnly = moduleRole === "Read";
	const resolveStepDisabled = (disabled: boolean) => isReadOnly ? false : disabled;
	
	const {	
		placementAgentVehicles,
		placementAgentActiveVehicles,
		vehicleId,
		isRegistrationNumberEnabled,
		currentStep,
		selectedPersonType,
		stepsFilled,
		isSuitabilityTestEnabled,
		suitabilityTestType,
		isInvestorStatusEnabled,
		//isRepresentativesPFEnabled,
		isInvestorWithoutNIFEnabled,
		isCRSEnabled,
		isFATCAEnabled,
		shouldSkipBeneficiariesGreater5Step,
		onChange,
		unlockNextStep,
		blackLogo,
		setSelectedPersonType,
		setVehicleId,
		contractLanguage
	} = useStepper({userIdInstitution, userEmail, institutionsAPI, investorsAPI, contractAPI, isInvestor});

	const shouldShowOptionalStep = (isEnabled: boolean | undefined) => isEnabled !== false;

	const resolveCurrentStepIndex = (
		stepsConfig: Array<{ actualIndex: number }>,
		actualStep: number,
	) => {
		const currentIndex = stepsConfig.findIndex((step) => step.actualIndex === actualStep);
		if (currentIndex >= 0) {
			return currentIndex;
		}

		const nextIndex = stepsConfig.findIndex((step) => step.actualIndex > actualStep);
		if (nextIndex >= 0) {
			return nextIndex;
		}

		return Math.max(stepsConfig.length - 1, 0);
	};

	const legalSteps = [
		{ actualIndex: 0, title: translations.datosFirmantes, disabled: false },
		...(shouldShowOptionalStep(isSuitabilityTestEnabled)
			? [{ actualIndex: 1, title: translations.suitabilityTest, disabled: resolveStepDisabled(stepsFilled <= 0 || !isSuitabilityTestEnabled) }]
			: []),
		{ actualIndex: 2, title: translations.datosDistribuidor, disabled: resolveStepDisabled(stepsFilled <= 1) },
		{ actualIndex: 3, title: translations.formularioInversor, disabled: resolveStepDisabled(stepsFilled <= 2) },
		{ actualIndex: 4, title: translations.representativeData, disabled: resolveStepDisabled(stepsFilled <= 3) },
		{ actualIndex: 5, title: translations.contactosInversor, disabled: resolveStepDisabled(stepsFilled <= 4) },
		...(shouldShowOptionalStep(isFATCAEnabled)
			? [{ actualIndex: 6, title: translations.clasificacionFatca, disabled: resolveStepDisabled(stepsFilled <= 5 || !isFATCAEnabled) }]
			: []),
		...(shouldShowOptionalStep(isCRSEnabled)
			? [{ actualIndex: 7, title: translations.clasificacionCRS, disabled: resolveStepDisabled(stepsFilled <= 6 || !isCRSEnabled) }]
			: []),
		...(!shouldSkipBeneficiariesGreater5Step
			? [{ actualIndex: 8, title: translations.beneficiariosMas5 || `${translations.beneficiarios} > 5%`, disabled: resolveStepDisabled(stepsFilled <= 7) }]
			: []),
		{ actualIndex: 9, title: translations.beneficiarios, disabled: resolveStepDisabled(stepsFilled <= 8) },
		{ actualIndex: 10, title: translations.titularFiscal, disabled: resolveStepDisabled(stepsFilled <= 9) },
		...(shouldShowOptionalStep(isInvestorStatusEnabled)
			? [{ actualIndex: 11, title: translations.estatusInversor, disabled: resolveStepDisabled(stepsFilled <= 10 || !isInvestorStatusEnabled) }]
			: []),
		{ actualIndex: 12, title: translations.documentacionSolicitada, disabled: resolveStepDisabled(stepsFilled <= 11) },
		{ actualIndex: 13, title: translations.sendContractToApproval, disabled: resolveStepDisabled(stepsFilled <= 12) },
	];

	const physicalSteps = [
		{ actualIndex: 0, title: translations.datosFirmantes, disabled: false },
		...(shouldShowOptionalStep(isSuitabilityTestEnabled)
			? [{ actualIndex: 1, title: translations.suitabilityTest, disabled: resolveStepDisabled(stepsFilled <= 0 || !isSuitabilityTestEnabled) }]
			: []),
		{ actualIndex: 2, title: translations.datosDistribuidor, disabled: resolveStepDisabled(stepsFilled <= 1) },
		{ actualIndex: 3, title: translations.formularioInversor, disabled: resolveStepDisabled(stepsFilled <= 2) },
		{ actualIndex: 4, title: translations.contactosInversor, disabled: resolveStepDisabled(stepsFilled <= 4) },
		...(shouldShowOptionalStep(isInvestorStatusEnabled)
			? [{ actualIndex: 5, title: translations.estatusInversor, disabled: resolveStepDisabled(stepsFilled <= 5 || !isInvestorStatusEnabled) }]
			: []),
		{ actualIndex: 6, title: translations.documentacionSolicitada, disabled: resolveStepDisabled(stepsFilled <= 6) },
		{ actualIndex: 7, title: translations.sendContractToApproval, disabled: resolveStepDisabled(stepsFilled <= 7) },
	];

	const legalCurrentStep = resolveCurrentStepIndex(legalSteps, currentStep);
	const physicalCurrentStep = resolveCurrentStepIndex(physicalSteps, currentStep);

	const onLegalStepChange = (stepIndex: number) => {
		const targetStep = legalSteps[stepIndex];
		if (targetStep) {
			onChange(targetStep.actualIndex);
		}
	};

	const onPhysicalStepChange = (stepIndex: number) => {
		const targetStep = physicalSteps[stepIndex];
		if (targetStep) {
			onChange(targetStep.actualIndex);
		}
	};

	return (
		<div className="stepper">
			{userIdInstitution !== 0
				? <>
					{selectedPersonType === 2 ?
					/* Persona Juridica */
						<div className="main-content">
							<div className="stepper-wrapper">
								<div className="stepper-sidebar-logo">
									<img
										src={blackLogo}
										alt="colocador-logo"
									/>
								</div>
								<Steps direction="vertical" current={legalCurrentStep} onChange={onLegalStepChange} >
									{legalSteps.map((step) => (
										<Step key={step.actualIndex} title={step.title} disabled={step.disabled} />
									))}
								</Steps>							
							</div>
							{currentStep === 0
								? <InvestorData
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									selectedPersonType={selectedPersonType}
									translations={translations}
									placementAgentVehicles={placementAgentVehicles}
									placementAgentActiveVehicles={placementAgentActiveVehicles}
									clientId={clientId}
									setSelectedPersonType={setSelectedPersonType}
									unlockNextStep={unlockNextStep}
									onVehicleChange={setVehicleId}
									userIdInstitution={userIdInstitution}
									language={language}
									contractLanguage={contractLanguage}
									isInvestor={isInvestor}
									isReadOnly={isReadOnly}
								/>
								: null
							}
							{currentStep === 1 && isSuitabilityTestEnabled === true
								? <SuitabilityTest
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									translations={translations}
									clientId={clientId}
									moduleRole={moduleRole}
									unlockNextStep={unlockNextStep}
									suitabilityTestType={suitabilityTestType}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 2
								? <DistributorData
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 3
								? <LegalInvestorForm
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									language={language}
									isInvestorWithoutNIFEnabled={isInvestorWithoutNIFEnabled}
									hideCommercialRegister={isRegistrationNumberEnabled === false}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 4
								? <RepresentativesForm
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									language={language}
									personType={selectedPersonType}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 5
								? <Contacts
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 6
								? <Fatca
									baseURL={baseURL}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									baseURLExternal={baseURLExternal}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 7
								? <CRS
									baseURL={baseURL}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									baseURLExternal={baseURLExternal}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 8
								? <Beneficiaries
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									language={language}
									contractLanguage={contractLanguage}
									mode="lite"
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 9
								? <Beneficiaries
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									language={language}
									contractLanguage={contractLanguage}
									mode="full"
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 10
								? <TaxHolder
									baseURL={baseURL}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									baseURLExternal={baseURLExternal}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 11
								? <LegalInvestorStatus
									baseURL={baseURL}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									baseURLExternal={baseURLExternal}
									userIdInstitution={userIdInstitution}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 12
								? <LegalInvestorDocuments
									baseURL={baseURL}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									baseURLExternal={baseURLExternal}
									userIdInstitution={userIdInstitution}
									language={language}
									moduleRole={moduleRole}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 13
								? <CreateSignature
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									translations={translations}
									clientId={clientId}
									isInvestor={isInvestor}
									moduleRole={moduleRole}
									userIdInstitution={userIdInstitution}
								/> : null 
							}
						</div>
						:
						/* Persona Fisica */
						<div className="main-content">
							<div className="stepper-wrapper">
								<div className="stepper-sidebar-logo">
									<img
										src={blackLogo}
										alt="colocador-logo"
									/>
								</div>
								<Steps direction="vertical" current={physicalCurrentStep} onChange={onPhysicalStepChange} >
									{physicalSteps.map((step) => (
										<Step key={step.actualIndex} title={step.title} disabled={step.disabled} />
									))}
								</Steps>
							</div>
							{currentStep === 0
								? <InvestorData
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									selectedPersonType={selectedPersonType}
									translations={translations}
									language={language}
									contractLanguage={contractLanguage}
									placementAgentVehicles={placementAgentVehicles}
									placementAgentActiveVehicles={placementAgentActiveVehicles}
									clientId={clientId}
									setSelectedPersonType={setSelectedPersonType}
									unlockNextStep={unlockNextStep}
									onVehicleChange={setVehicleId}
									userIdInstitution={userIdInstitution}
									isInvestor={isInvestor}
									isReadOnly={isReadOnly}
								/>
								: null
							}
							{currentStep === 1 && isSuitabilityTestEnabled === true
								? <SuitabilityTest
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									translations={translations}
									clientId={clientId}
									moduleRole={moduleRole}
									unlockNextStep={unlockNextStep}
									suitabilityTestType={suitabilityTestType}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 2
								? <DistributorData
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 3
								? <PhysicalInvestorForm
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									language={language}
									contractLanguage={contractLanguage}
									isInvestorWithoutNIFEnabled={isInvestorWithoutNIFEnabled}
									isReadOnly={isReadOnly}
								/> : null
							}
							{/* {currentStep === 4
								? <RepresentativesForm
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									language={language}
									personType={selectedPersonType}
								/> : null
							} */}
							{currentStep === 4
								? <Contacts
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 5
								? <PhysicalInvestorStatus
									baseURL={baseURL}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									baseURLExternal={baseURLExternal}
									userIdInstitution={userIdInstitution}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 6
								? <PhysicalInvestorDocuments
									baseURL={baseURL}
									translations={translations}
									clientId={clientId}
									unlockNextStep={unlockNextStep}
									baseURLExternal={baseURLExternal}
									userIdInstitution={userIdInstitution}
									language={language}
									moduleRole={moduleRole}
									isReadOnly={isReadOnly}
								/> : null
							}
							{currentStep === 7
								? <CreateSignature
									baseURL={baseURL}
									baseURLExternal={baseURLExternal}
									translations={translations}
									clientId={clientId}
									isInvestor={isInvestor}
									moduleRole={moduleRole}
									userIdInstitution={userIdInstitution}
								/> : null
							}
						</div>
					}
					</> : null
				}
		</div>
	);
};

export default Stepper;
